import { Box, Grid, LinearProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DatePicker from 'components/date-picker/date-picker';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import { InfoDeliveryWire } from 'components/users/user-wire-transfers/types';
import { ListingEntry } from 'graphql/listings/queries';
import { GQLUpdateParty, GQLUpdatePartyInfoPayload } from 'graphql/parties/mutations';
import React, { FC, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UseMutateFunction } from 'react-query';
import { NewUserType } from 'v2-types/user';

type generalInforFormProps = {
  contactID: string;
  kindList: ListingEntry[];
  updatePartyMutation: UseMutateFunction<
    GQLUpdateParty,
    unknown,
    GQLUpdatePartyInfoPayload,
    unknown
  >;
  isTitleCompany: boolean;
  isSaving?: boolean;
};

const useStyles = makeStyles((theme: any) => ({
  progressSlot: {
    height: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
}));

type formType = NewUserType & {
  paymentAccounts: InfoDeliveryWire[];
};

const GeneralInformationform: FC<generalInforFormProps> = ({
  contactID,
  kindList,
  updatePartyMutation,
  isTitleCompany,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { control } = useFormContext<formType>();
  const kind = useWatch({ control, name: 'kind' });
  const genders = useMemo(
    () => [
      {
        value: 'Male',
        text: t('common:male'),
      },
      {
        value: 'Female',
        text: t('common:female'),
      },
    ],
    [t]
  );
  return (
    <Box aria-busy={isSaving}>
      {/* Autosave-on-blur exception (DESIGN.md §5): signal without disabling so focus and the
          virtual keyboard stay on the next field. */}
      <Box className={classes.progressSlot}>
        {isSaving && <LinearProgress color="secondary" aria-label={`${t('contact:saving')}`} />}
      </Box>
      <Grid container direction="column" spacing={3}>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="first_name"
            rules={{ required: true }}
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                inputRef={ref}
                label={t('users:firstName')}
                handleBlur={() => {
                  updatePartyMutation({
                    id: contactID,
                    data: {
                      first_name: field.value,
                    },
                  });
                }}
                error={field.value === '' || field.value === null}
              />
            )}
          />
        </Grid>
        {kind === 'Person' && (
        <>
          <Grid item>
            <Controller
              control={control}
              name="middle_name"
              render={({ field: { ref, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  label={t('users:middleName')}
                  handleBlur={() => {
                    updatePartyMutation({
                      id: contactID,
                      data: {
                        middle_name: field.value,
                      },
                    });
                  }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="last_name"
              render={({ field: { ref, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  label={t('users:lastName')}
                  handleBlur={() => {
                    updatePartyMutation({
                      id: contactID,
                      data: {
                        last_name: field.value,
                      },
                    });
                  }}
                  error={field.value === '' || field.value === null}
                />
              )}
            />
          </Grid>
        </>
        )}
        <Grid item>
          <Controller
            name="kind"
            control={control}
            render={({ field: { ref, onBlur, ...field } }) => (
              <SelectField
                {...field}
                value={field.value || ''}
                data={(() => {
                  const base = isTitleCompany
                    ? [...kindList, { code: 'Title Company', description: 'Title Company' }]
                    : kindList;
                  if (kind && !base.some((o: ListingEntry) => o.code === kind)) {
                    return [...base, { code: kind, description: kind }];
                  }
                  return base;
                })()}
                dataKey="code"
                dataValue="code"
                dataText="description"
                label={t('users:kind')}
                disabled={isTitleCompany}
                handleBlur={() => {
                  if (field.value && field.value !== '') {
                    updatePartyMutation({
                      id: contactID,
                      data: {
                        kind: field.value,
                      },
                    });
                  }
                }}
                error={field.value === '' || field.value === null}
                handleChange={field.onChange}
              />
            )}
          />
        </Grid>
        {kind === 'Person' && (
        <>
          <Grid item>
            <Controller
              control={control}
              name="birth"
              render={({ field: { ref, ...field } }) => (
                <DatePicker
                  {...field}
                  inputRef={ref}
                  value={field.value}
                  label={t('users:date-of-birth')}
                  onChange={(e) => {
                    field.onChange(e);
                    if (e) {
                      updatePartyMutation({
                        id: contactID,
                        data: {
                          birth: e,
                        },
                      });
                    }
                  }}
                  InputProps={{
                    onBlur: () => {
                      if (field.value && field.value !== '') {
                        updatePartyMutation({
                          id: contactID,
                          data: {
                            birth: field.value,
                          },
                        });
                      }
                    },
                  }}
                  handleBlur={() => {
                    if (field.value && field.value !== '') {
                      updatePartyMutation({
                        id: contactID,
                        data: {
                          birth: field.value,
                        },
                      });
                    }
                  }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              name="gender"
              control={control}
              render={({ field: { ref, onChange, onBlur, ...field } }) => (
                <SelectField
                  {...field}
                  value={field.value ?? ''}
                  inputRef={ref}
                  data={genders}
                  dataKey="value"
                  dataValue="value"
                  dataText="text"
                  handleChange={(value) => {
                    onChange(value);
                  }}
                  label={t('users:gender')}
                  handleBlur={() => {
                    updatePartyMutation({
                      id: contactID,
                      data: {
                        gender: field.value,
                      },
                    });
                  }}
                />
              )}
            />
          </Grid>
        </>
        )}
        <Grid item>
          <Controller
            control={control}
            name="ssn"
            render={({ field: { ref, ...field } }) => (
              <TextField
                {...field}
                inputRef={ref}
                label={kind !== 'Person' ? 'EIN' : 'SSN/ATIN/ITIN/PTIN'}
                handleBlur={() => {
                  updatePartyMutation({
                    id: contactID,
                    data: {
                      ssn: field.value,
                    },
                  });
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeneralInformationform;
